import { logger } from "../../utils/logger";
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { Plus, Edit2, Trash2, Trophy, Users } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyEntry = {
  rank: 1,
  player_name: "",
  points: 0,
  wins: 0,
  tournament: "",
  date: "",
};

export default function LeaderboardManager() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState(emptyEntry);
  const { getAuthHeaders } = useAuth();

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/leaderboard`);
      setEntries(response.data);
    } catch (err) {
      logger.error("Error fetching leaderboard:", err);
      toast.error("Failed to load leaderboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  const openCreateDialog = () => {
    setEditingEntry(null);
    setFormData({ ...emptyEntry, rank: entries.length + 1 });
    setDialogOpen(true);
  };

  const openEditDialog = (entry) => {
    setEditingEntry(entry);
    setFormData({
      rank: entry.rank,
      player_name: entry.player_name,
      points: entry.points || 0,
      wins: entry.wins || 0,
      tournament: entry.tournament || "",
      date: entry.date || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.player_name || !formData.rank) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      if (editingEntry) {
        await axios.put(`${API}/leaderboard/${editingEntry.id}`, formData, getAuthHeaders());
        toast.success("Entry updated!");
      } else {
        await axios.post(`${API}/leaderboard`, formData, getAuthHeaders());
        toast.success("Entry created!");
      }
      setDialogOpen(false);
      fetchEntries();
    } catch (err) {
      toast.error("Failed to save entry");
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm("Are you sure you want to delete this entry?")) return;

    try {
      await axios.delete(`${API}/leaderboard/${entryId}`, getAuthHeaders());
      toast.success("Entry deleted!");
      fetchEntries();
    } catch (err) {
      toast.error("Failed to delete entry");
    }
  };

  if (loading) {
    return <div className="text-gold animate-pulse">Loading leaderboard...</div>;
  }

  return (
    <div data-testid="leaderboard-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl text-white mb-2">Leaderboard Manager</h1>
          <p className="text-white/60">Manage player rankings and scores</p>
        </div>
        <Button
          onClick={openCreateDialog}
          className="bg-gold text-black hover:bg-gold-dark"
          data-testid="add-leaderboard-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Player
        </Button>
      </div>

      {/* Entries List */}
      {entries.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <Trophy className="w-12 h-12 text-gold/30 mx-auto mb-4" />
          <p className="text-white/60">No players on the leaderboard yet. Add your first player!</p>
        </div>
      ) : (
        <div className="glass-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10 bg-black/30">
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-gold">Rank</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-gold">Player</th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-wider text-gold">Points</th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-wider text-gold">Wins</th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-wider text-gold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr
                  key={entry.id}
                  className="border-b border-white/5 hover:bg-white/5"
                  data-testid={`leaderboard-row-${entry.id}`}
                >
                  <td className="px-4 py-4">
                    <span className={`font-heading text-xl ${entry.rank <= 3 ? 'text-gold' : 'text-white/60'}`}>
                      #{entry.rank}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center">
                        <Users className="w-4 h-4 text-gold" />
                      </div>
                      <span className="text-white font-medium">{entry.player_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-right text-white">{entry.points?.toLocaleString()}</td>
                  <td className="px-4 py-4 text-right text-gold">{entry.wins}</td>
                  <td className="px-4 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditDialog(entry)}
                        className="border-white/20 text-white hover:border-gold hover:text-gold"
                        data-testid={`edit-leaderboard-${entry.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(entry.id)}
                        className="border-white/20 text-white hover:border-red-500 hover:text-red-500"
                        data-testid={`delete-leaderboard-${entry.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-casino-dark border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingEntry ? "Edit Player" : "Add Player"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="leaderboard-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/80">Rank *</Label>
                <Input
                  type="number"
                  value={formData.rank}
                  onChange={(e) => setFormData({ ...formData, rank: parseInt(e.target.value) || 1 })}
                  className="bg-casino-secondary border-white/10 text-white mt-1"
                  min="1"
                  data-testid="rank-input"
                />
              </div>
              <div>
                <Label className="text-white/80">Wins</Label>
                <Input
                  type="number"
                  value={formData.wins}
                  onChange={(e) => setFormData({ ...formData, wins: parseInt(e.target.value) || 0 })}
                  className="bg-casino-secondary border-white/10 text-white mt-1"
                  min="0"
                  data-testid="wins-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-white/80">Player Name *</Label>
              <Input
                value={formData.player_name}
                onChange={(e) => setFormData({ ...formData, player_name: e.target.value })}
                className="bg-casino-secondary border-white/10 text-white mt-1"
                placeholder="Enter player name"
                data-testid="player-name-input"
              />
            </div>

            <div>
              <Label className="text-white/80">Points</Label>
              <Input
                type="number"
                value={formData.points}
                onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) || 0 })}
                className="bg-casino-secondary border-white/10 text-white mt-1"
                min="0"
                data-testid="points-input"
              />
            </div>

            <div>
              <Label className="text-white/80">Tournament</Label>
              <Input
                value={formData.tournament}
                onChange={(e) => setFormData({ ...formData, tournament: e.target.value })}
                className="bg-casino-secondary border-white/10 text-white mt-1"
                placeholder="e.g., Saturday $1K Guarantee"
                data-testid="tournament-input"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="flex-1 border-white/20 text-white"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gold text-black hover:bg-gold-dark"
                data-testid="leaderboard-submit-btn"
              >
                {editingEntry ? "Update" : "Add Player"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
